import { useState, type ReactNode } from 'react';

import type { clientTypes } from '@bedc/client/types';

import { applyBrand } from '../utils/brandDocumentUtils';
import { getBrandLogos } from '../utils/brandLogoUtils';
import { resolveBrandId, getBrandProfile } from '../utils/brandUtils';

import { BrandContext } from './BrandContext';

// ----------------------------------------------------------------------------
// TYPES
// ----------------------------------------------------------------------------

export namespace BrandProvider {
	export type Props = {
		/** Application content that consumes the active brand. */
		children: ReactNode;
	};
}

// ----------------------------------------------------------------------------
// COMPONENTS
// ----------------------------------------------------------------------------

export const BrandProvider = ({ children }: BrandProvider.Props) => {
	'use memo';

	// Data
	// ------------------------------------------------------------------------

	// Local.
	const [initialId] = useState(() => resolveBrandId(document.documentElement.dataset.brand));
	const [id, setId] = useState(initialId);
	const brand = { id, ...getBrandProfile(id), ...getBrandLogos(id) };

	// Handlers
	// ------------------------------------------------------------------------

	/** Apply an in-memory override without changing user scope. */
	const setBrand = (next: clientTypes.brand.Id) => {
		const selected = resolveBrandId(next);
		if (selected === id) return;
		applyBrand(selected);
		setId(selected);
	};

	/** Restore the brand originally delivered by the host. */
	const resetBrand = () => setBrand(initialId);

	// Template
	// ------------------------------------------------------------------------

	return (
		<BrandContext.Provider value={{ brand, setBrand, resetBrand }}>
			{children}
		</BrandContext.Provider>
	);
};
